import React, { useState, useEffect, useCallback, useContext, useRef, useLayoutEffect, useState as useState2 } from "react";
import { createPortal } from "react-dom";
import { useParams } from "react-router-dom";
import openSocket from "../../services/socket-io";
// emoji-mart 5 não traz mais CSS próprio nem exporta React: o componente vem
// do pacote @emoji-mart/react e os emojis do @emoji-mart/data.
import Picker from "@emoji-mart/react";
import emojiData from "@emoji-mart/data";
import {
  Paperclip,
  Smile,
  Send,
  X,
  Mic,
  MoreVertical,
  XCircle,
  CheckCircle2,
  Loader2,
  Images,
  Clock,
  FileText,
  Sticker,
  Zap,
  MapPin,
  ChevronDown,
  Camera,
  UserRound,
  Type,
  Lock,
  Eye,
  Check,
  AlertTriangle,
  Image as ImageIcon,
  Video as VideoIcon,
  Music,
  FileIcon,
  FileArchive,
  FileSpreadsheet,
  Plus,
} from "lucide-react";

import { Button } from "../ui/button";
import { Switch } from "../ui/switch";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import { Badge } from "../ui/badge";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { cn } from "../../lib/utils";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import { touchTicket } from "../../services/recentContext";
import RecordingTimer from "./RecordingTimer";
import { ReplyMessageContext } from "../../context/ReplyingMessage/ReplyingMessageContext";
import { AuthContext } from "../../context/Auth/AuthContext";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import { useThemeContext } from "../../context/DarkMode";
import { toast } from "react-toastify";
import toastError from "../../errors/toastError";
import MediaLibraryPicker from "../MediaLibraryPicker";
import FigurinhaPicker from "../FigurinhaPicker";
import EnviarTemplateModal from "../EnviarTemplateModal";
import AssistenteDeTexto from "./AssistenteDeTexto";
import PreviaDeAnexos from "./PreviaDeAnexos";
import { getPublicFileUrl } from "../../config";
import { extensaoDe } from "../../helpers/tiposDeArquivo";
import EnviarLocalizacaoModal from "../EnviarLocalizacaoModal";
import CameraModal from "../CameraModal";
import EnviarContatoModal from "../EnviarContatoModal";
import BarraDeFormatacao, { aplicarMarca } from "./BarraDeFormatacao";
import { paraWav } from "./wav";
import {
  ACCEPT_UPLOAD,
  problemaDoArquivo,
  TIMEOUT_ENVIO_MEDIA_MS,
  LIMITE_ENVIO_EM_PARTES_BYTES,
  TAMANHO_PARTE_BYTES,
  TIMEOUT_PARTE_MS
} from "../../constants/uploads";
import QuickAnswersModal from "../QuickAnswersModal";

// Gravação de voz com o MediaRecorder do próprio navegador.
//
// Antes era a mic-recorder-to-mp3, que não funciona mais aqui: ela espera o
// codificador `lamejs` como variável global (formato UMD) e o bundler atual
// carrega tudo como módulo ES. O resultado era `ReferenceError: Lame is not
// defined` na primeira tentativa — o gravador nunca chegava a existir, e o
// botão de áudio simplesmente não fazia nada.
//
// O MediaRecorder é padrão do navegador, não tem dependência e grava em
// Opus — que é justamente o codec que o WhatsApp usa para mensagem de voz.
// O MP3 anterior era convertido de qualquer forma antes de sair.

// Formatos por ordem de preferência; o navegador escolhe o primeiro que
// souber gravar. Safari só tem mp4/aac, Chrome e Firefox preferem opus.
const FORMATOS = [
  { mime: "audio/ogg;codecs=opus", ext: "ogg" },
  { mime: "audio/webm;codecs=opus", ext: "webm" },
  { mime: "audio/mp4", ext: "m4a" },
  { mime: "audio/webm", ext: "webm" }
];

const escolherFormato = () => {
  if (typeof MediaRecorder === "undefined") return null;

  return (
    FORMATOS.find((f) => MediaRecorder.isTypeSupported(f.mime)) || {
      mime: "",
      ext: "webm"
    }
  );
};

const MessageInput = ({ ticketStatus, isGroup, ticket }) => {
  const { ticketId } = useParams();

  const [medias, setMedias] = useState([]);
  const [inputMessage, setInputMessage] = useState("");
  const [showEmoji, setShowEmoji] = useState(false);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [figurinhasAberto, setFigurinhasAberto] = useState(false);
  // Todas as respostas prontas da empresa, para a faixa de atalhos acima do
  // campo. Antes elas só existiam para quem soubesse digitar "/": quem não
  // sabia do atalho nunca descobriu que a tela de respostas prontas servia
  // para alguma coisa.
  const [atalhos, setAtalhos] = useState([]);
  const [atalhosAbertos, setAtalhosAbertos] = useLocalStorage(
    "faixaDeAtalhos",
    true,
  );
  const [loading, setLoading] = useState(false);

  // Modal de edição / nova resposta rápida.
  //
  // - Clique normal no chip: continua preenchendo o campo (comportamento
  //   antigo, ninguém perde o hábito).
  // - Ícone de lápis no chip (aparece no hover) OU botão "+ Criar resposta":
  //   abre o modal para editar / criar.
  const [qrAberto, setQrAberto] = useState(false);
  const [qrEditandoId, setQrEditandoId] = useState(null);
  const [qrInicial, setQrInicial] = useState(null);

  // Função idempotente de recarregar a faixa de atalhos do zero. Chamada após
  // PUT / POST / DELETE no modal de edição.
  const recarregarAtalhos = useCallback(async () => {
    try {
      const todas = [];
      let pagina = 1;
      let temMais = true;
      while (temMais && pagina <= 5) {
        const { data } = await api.get("/quickAnswers/", {
          params: { pageNumber: pagina },
        });
        todas.push(...(data?.quickAnswers || []));
        temMais = Boolean(data?.hasMore);
        pagina += 1;
      }
      setAtalhos(todas);
    } catch (err) {
      // Falha silenciosa: a faixa só some, sem toast de erro em cada abertura.
      setAtalhos((atual) => atual);
    }
  }, []);

  // Guarda a resposta pronta escolhida até a mensagem sair, para então mover
  // o cartão do cliente no quadro. Editar o texto antes de mandar não desfaz
  // a escolha: a resposta continua sendo aquela, com outras palavras.
  const [respostaAplicavel, setRespostaAplicavel] = useState(null);
  const [recording, setRecording] = useState(false);
  const [quickAnswers, setQuickAnswer] = useState([]);
  const [typeBar, setTypeBar] = useState(false);
  const [arrastandoArquivo, setArrastandoArquivo] = useState(false);
  const [localizacaoAberta, setLocalizacaoAberta] = useState(false);
  const [cameraAberta, setCameraAberta] = useState(false);
  const [contatoAberto, setContatoAberto] = useState(false);
  const [formatacaoAberta, setFormatacaoAberta] = useLocalStorage(
    "barraDeFormatacao",
    false,
  );
  // Removido placeholder não utilizado (o estado do privado real fica abaixo,
  // com trava por perfil e valor inicial default ligado).
  // Assinatura da mensagem (botão Assinar no dropdown).
  //
  // A preferência GENUÍNA vem do banco (user.signMessages) no retorno do
  // /auth/login. Para não regredir instalações antigas, quem ainda não tem
  // a coluna no banco usa o valor do localStorage (ou true por padrão).
  //
  // A empresa pode FORÇAR assinatura SOMENTE para ATENDENTES COMUNS (perfil
  // user ou equivalente) usando o setting force_sign_messages = enabled.
  // Nesse caso:
  //   - Administradores e supervisores continuam podendo ligar e desligar
  //     a assinatura como quiserem.
  //   - Atendentes comuns ficam com o switch DESABILITADO e a assinatura
  //     SEMPRE ligada.
  // O perfil vem em user.profile (admin / agent / supervisor / user).
  const { user, handleUpdateSignMessages } = useContext(AuthContext);
  const profile = (user?.profile || "user").toLowerCase();
  const isAtendenteComum =
    profile === "user" || profile === "agent" || profile === "atendente";

  // Regra do switch "Privado":
  // - Admin / supervisor / administrator: LIVRE. Default = DESLIGADO
  //   (mensagem WhatsApp normal por padrão). O usuário liga manualmente
  //   quando quiser escrever uma nota interna. Podem ligar e desligar livre.
  // - Atendente comum (qualquer outro perfil): FORÇADO E TRAVADO em true,
  //   NUNCA responde o cliente por WhatsApp normal — só por nota privada.
  //   Também trava o envio de mídia / localização / contato / figurinha / etc
  //   por WhatsApp (o resto do handleSendMessage já está ligado no `privado`).
  // - Usuário MENCIONADO (não dono, não admin/supervisor): TAMBÉM fica só em
  //   modo privado. Ele vê o ticket para responder a nota interna, mas NÃO
  //   pode enviar mensagem WhatsApp normal para o cliente (modo leitura).
  const ehAdminOuSupervisor =
    profile === "admin" ||
    profile === "supervisor" ||
    profile === "administrator";
  // "Dono do ticket" = eu sou o dono registrado (userId === meu.id) OU o ticket
  // NÃO TEM DONO AINDA (userId null/undefined). No caso sem dono (ticket em
  // "aguardando" e ninguém clicou em Aceitar ainda), o usuário que está
  // visualizando agora tem DIREITO de assumir automaticamente ao enviar a
  // primeira mensagem normal (WhatsApp). O backend também confirma essa
  // operação gravando userId e status=open. Sem essa exceção: atendente comum
  // (não admin) ficava SEMPRE com o switch "Privado (obrigatório)" LIGADO e
  // TRAVADO, pois atendenteComum = !admin && !dono && !grupo = true, mesmo
  // que ninguém estivesse atendendo.
  // GRUPOS: qualquer atendente que tenha acesso (escopo cargo / fila) pode
  // falar normal WhatsApp no grupo. NÃO existe "dono exclusivo" de grupo.
  // Isso evita que grupos com userId definido (criador do grupo) acionem
  // travaPrivado para atendentes que não são o criador. Para conversas
  // INDIVIDUAIS: dono real OU userId === NULL (sem dono, vai assumir 1a msg).
  const ehDonoDoTicket =
    ticket && user
      ? ticket?.isGroup
        ? true
        : Number(ticket.userId) === Number(user.id) || ticket.userId === null
      : true;
  // Só ativa modo "só nota privada" de MENCIONADO se:
  //  (1) Existe menção NÃO DISPENSADA para este usuário. Pode ser:
  //       - hasUnreadMentionForUser (menção NOVA ainda não abri)
  //       - hasMentionForUser (qualquer menção ainda não dispensada, mesmo
  //         que eu já abri para ler e visto = true)
  //       - Number(unreadMentions) > 0 (contador > 0 = existem menções não
  //         dispensadas vindo da lista)
  //      Antes usava só hasUnreadMentionForUser e a trava caía assim que o
  //      usuário abria o ticket para ler a nota (seenAt = NOW) — mesmo que
  //      ele não tivesse dispensado explicitamente. Resultado: ele podia
  //      mandar mensagem normal WhatsApp para o cliente por engano antes de
  //      "Aceitar conversa" ou "Fechar mensagem privada".
  //  (2) O usuário NÃO é o DONO do ticket (não importa a menção). Se ele "Aceitou"
  //      o ticket e virou dono → libera WhatsApp normal como qualquer atendente comum.
  //  (3) O usuário NÃO é admin/supervisor.
  const temMencaoNaoDispensada =
    ticket && (
      Boolean(ticket.hasUnreadMentionForUser) ||
      Boolean(ticket.hasMentionForUser) ||
      Number(ticket.unreadMentions) > 0
    );
  const mencionadoNaoDonoNemAdmin =
    temMencaoNaoDispensada && !ehDonoDoTicket && !ehAdminOuSupervisor;

  // travaPrivado é OR: atendente comum (não grupo, não dono) SEMPRE (regra
  // legada) || mencionado encaixa. Mas SE ele é DONO do ticket, NÃO travar —
  // mesmo que seja atendente comum.
  //
  // GRUPOS (isGroup === true): atendentes comuns NÃO SÃO OBRIGADOS a usar
  // só nota privada. Em grupo todo mundo responde por WhatsApp para os
  // membros, a regra de "só nota interna" é só para conversas INDIVIDUAIS
  // de cliente que já tem dono e o atendente comum não é o dono. A
  // trava para MENCIONADO (mencionadoNaoDonoNemAdmin) continua valendo
  // tanto para grupo quanto individual (se ele foi mencionado com @Nome
  // em nota interna e ainda não dispensou).
  const atendenteComum =
    ehAdminOuSupervisor === false &&
    ehDonoDoTicket === false &&
    !ticket?.isGroup;
  const travaPrivado = atendenteComum || mencionadoNaoDonoNemAdmin;
  const valorInicialPrivado = travaPrivado ? true : false;
  const [privadoEstado, setPrivadoEstado] = useState(valorInicialPrivado);
  // Computado final. Para atendente comum é SEMPRE true, não importa o estado.
  const privado = travaPrivado ? true : Boolean(privadoEstado);
  const setPrivado = (checked) => {
    if (travaPrivado) {
      // Trava ativa: força o estado local para true e ignora a tentativa.
      setPrivadoEstado(true);
      return;
    }
    setPrivadoEstado(Boolean(checked));
  };
  // Sempre que TROCAR DE TICKET (ticketId mudar), reseta o modo privado para
  // o valor padrão do perfil. Sem isso: o estado de "privado" persistia
  // entre tickets. Ex: usuário estava em modo privado no ticket A, ia para o
  // ticket B e já chegava com privado LIGADO sem pedir.
  useEffect(() => {
    if (travaPrivado) {
      setPrivadoEstado(true);
    } else {
      setPrivadoEstado(false);
    }
    // // Troca de ticket também limpa a lista de menções do banner.
  }, [ticketId, travaPrivado, ticket?.id, ticket?.userId, temMencaoNaoDispensada]);
  useEffect(() => {
    if (travaPrivado) {
      setPrivadoEstado(true);
    }
  }, [user?.id, travaPrivado]);

  const [forceSignMessagesEnabled, setForceSignMessagesEnabled] = useState(false);
  // Só efetivamente TRAVA se a empresa ligou E o usuário for atendente.
  // Admin e supervisor ficam sempre livres, independente da setting.
  const travaBloqueiaAssinatura =
    forceSignMessagesEnabled === true && isAtendenteComum;
  // Só bloqueia a UI QUANDO A TRAVA FOR REALMENTE LIGADA (true). NÃO
  // bloqueia mais enquanto carrega — isso invertia a lógica e o atendente
  // ficava impossibilitado de desligar mesmo com a configuração DESLIGADA.
  const switchAssinarDesabilitado = travaBloqueiaAssinatura === true;

  const [signMessageStorage, setSignMessageStorage] = useLocalStorage("signOption", true);
  const valorInicialSign =
    travaBloqueiaAssinatura
      ? true
      : typeof user?.signMessages === "boolean"
        ? user.signMessages
        : Boolean(signMessageStorage);
  const [signMessageSessao, setSignMessageSessao] = useState(valorInicialSign);
  // Sincroniza sempre que entrar login novo / trocar usuário / alterar a
  // setting force_sign_messages da empresa via socket / outro dispositivo.
  useEffect(() => {
    if (travaBloqueiaAssinatura === true) {
      // Empresa bloqueou: FORÇA tudo para true imediatamente — NÃO importa
      // o que o usuário tinha antes (banco, localStorage, sessão).
      setSignMessageSessao(true);
      setSignMessageStorage(true);
      if (user?.id) {
        handleUpdateSignMessages(true).catch(() => undefined);
      }
    } else if (typeof user?.signMessages === "boolean") {
      setSignMessageSessao(Boolean(user.signMessages));
      setSignMessageStorage(Boolean(user.signMessages));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, user?.signMessages, travaBloqueiaAssinatura]);
  // Computado final — NUNCA depende só de signMessageSessao se a trava está
  // ligada. Até aqui o valor já está correto, e é usado TANTO no Switch
  // quanto no texto enviado (handleSendMessage / corpo da mensagem / mídia).
  //
  // REGRA INQUEBRÁVEL:
  // - Se a empresa ligou o "force_sign_messages" E o usuário NÃO é admin/
  //   supervisor (é atendente comum), a assinatura É OBRIGATÓRIAMENTE true,
  //   NÃO IMPORTA o que estiver gravado no banco do usuário, no localStorage
  //   ou na sessão.
  // - Admin e supervisor continuam 100% livres, sempre.
  const signMessage = travaBloqueiaAssinatura === true ? true : Boolean(signMessageSessao);
  const setSignMessage = async (checked) => {
    const valor = Boolean(checked);
    // Trava total para atendente comum quando a empresa bloqueou.
    // NÃO permite qualquer alteração (nem false, nem verdadeiro desnecessário
    // que passaria para algum lado). Se ainda está carregando (undefined) e
    // é atendente, também bloqueia (igual ao switch).
    if (switchAssinarDesabilitado) {
      // Recompõe o valor correto imediatamente, caso o Label tenha
      // disparado o evento mesmo com Switch disabled.
      if (travaBloqueiaAssinatura) {
        setSignMessageSessao(true);
        setSignMessageStorage(true);
        if (user?.id) {
          handleUpdateSignMessages(true).catch(() => undefined);
        }
      }
      return;
    }
    setSignMessageSessao(valor);
    // Também atualiza o localStorage para quem não migrou ainda continuar
    // vendo o mesmo valor ao recarregar.
    setSignMessageStorage(valor);
    if (user?.id) {
      // Não espera resolver para não travar a UI. Em caso de erro no banco,
      // o estado local já está certo para esta sessão, e ao recarregar vai
      // voltar o valor real (o usuário não fica no escuro).
      handleUpdateSignMessages(valor).catch(() => undefined);
    }
  };

  // Busca o setting "force_sign_messages" da empresa. Se for "enabled" a
  // empresa quer bloquear os atendentes comuns de desligarem a assinatura.
  // Persistido no banco Settings da empresa (Setting.key = force_sign_messages).
  useEffect(() => {
    if (!user || !user.companyId) return undefined;
    let cancelado = false;
    (async () => {
      try {
        const { data } = await api.get("/settings");
        if (cancelado) return;
        const f = data.find(
          (s) => String(s.key).toLowerCase() === "force_sign_messages",
        );
        setForceSignMessagesEnabled(
          !!(f && String(f.value).toLowerCase() === "enabled"),
        );
      } catch (err) {
        // Sem permissão / sem network: mantém false (se comporta igual antes).
      }
    })();
    return () => {
      cancelado = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.companyId]);

  useEffect(() => {
    if (!user || !user.companyId) return undefined;
    const socket = openSocket();
    const handleSettings = (data) => {
      if (
        data &&
        data.action === "update" &&
        data.setting &&
        String(data.setting.key).toLowerCase() === "force_sign_messages"
      ) {
        setForceSignMessagesEnabled(
          !!(data.setting.value && String(data.setting.value).toLowerCase() === "enabled"),
        );
      }
    };
    socket.on("settings", handleSettings);
    return () => {
      try { socket.off("settings", handleSettings); } catch (_) {}
    };
  }, [user?.companyId]);

  // Participantes do grupo e quem foi marcado com @ nesta mensagem.
  const [participantes, setParticipantes] = useState([]);
  const [mencionados, setMencionados] = useState([]);
  const [listaDeMencao, setListaDeMencao] = useState(null);
  // Usuários da equipe (para @ em notas privadas/internas).
  const [usersEquipe, setUsersEquipe] = useState([]);
  // IDs dos User marcados (@) na nota interna PRIVADA (serão enviados ao backend).
  const [internalMentions, setInternalMentions] = useState([]);
  // Dialog para selecionar atendentes a notificar quando a pessoa clica em
  // "Privado" e liga a chave. Abre automaticamente para ele escolher quem vai
  // ver a nota interna e recebe a notificação.
  const [seletorNotificarAberto, setSeletorNotificarAberto] = useState(false);
  const [mencionadosTemp, setMencionadosTemp] = useState([]);
  // Valor do privado NO CLIQUE ANTERIOR — usamos para detectar a transição
  // OFF → ON e abrir o Dialog automaticamente sem repetir em todo render.
  const privadoAnteriorRef = useRef(privado);
  useEffect(() => {
    const antes = privadoAnteriorRef.current;
    privadoAnteriorRef.current = privado;
    // Só abre o selector no clique que LIGA o privado.
    if (privado === true && antes === false && !travaPrivado) {
      // Pré-carrega os valores: mantém quem já tinha sido selecionado antes.
      setMencionadosTemp([...internalMentions]);
      setSeletorNotificarAberto(true);
    }
  }, [privado, travaPrivado, internalMentions]);
  // Resetar menções temp e fechar Dialog quando desliga privado.
  useEffect(() => {
    if (privado === false) {
      setMencionadosTemp([]);
      setSeletorNotificarAberto(false);
    }
  }, [privado]);
  // Com que nome esta conversa vai assinada. `opcoes` são os nomes
  // alternativos da pessoa; `efetivo` é o que vale agora (o fixado nesta
  // conversa, o da fila, ou o nome do cadastro).
  const [assinatura, setAssinatura] = useState({
    efetivo: "",
    aliasId: null,
    opcoes: [],
  });
  const inputRef = useRef();
  // Gravação em curso: o MediaRecorder, o stream do microfone e os pedaços.
  const gravadorRef = useRef(null);
  const pedacosRef = useRef([]);
  const emojiBoxRef = useRef();
  // Ancora visual para popups flutuantes.
  //
  // - `ancoraBarrasRef`: envolve TODA a linha (botões da esquerda + textarea
  //   + botão enviar da direita). Usado por EmojiPicker e FigurinhaPicker,
  //   que no WhatsApp abrem alinhados à ESQUERDA, onde fica o botão 😀.
  //   Resolve o "abrir desalinhado em tela maior".
  // - `ancoraPopupsRef`: APENAS a `relative min-w-0 flex-1` do textarea.
  //   Usado pelos autocompletes @mencao e /respostas rápidas, que devem ficar
  //   exatamente acima do textarea, não à esquerda.
  //
  // Ambos abrem via createPortal(document.body) para sair do stacking context
  // confinado do `@container` do wrapper MessageInput.
  const ancoraBarrasRef = useRef();
  const ancoraPopupsRef = useRef();
  const [, setTickPopups] = useState(0);
  useLayoutEffect(() => {
    const recalcular = () => setTickPopups((t) => (t + 1) % 1000000);
    recalcular();
    window.addEventListener("resize", recalcular);
    window.addEventListener("scroll", recalcular, true);
    const ob =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(recalcular)
        : null;
    if (ancoraBarrasRef.current) ob?.observe(ancoraBarrasRef.current);
    if (ancoraPopupsRef.current) ob?.observe(ancoraPopupsRef.current);
    if (inputRef.current) ob?.observe(inputRef.current);
    return () => {
      window.removeEventListener("resize", recalcular);
      window.removeEventListener("scroll", recalcular, true);
      ob?.disconnect();
    };
  }, [showEmoji, figurinhasAberto, listaDeMencao !== null, typeBar]);

  const PortalPopup = ({
    children,
    z = 70,
    mb = 2,
    fullWidthOnMobile = true,
    smWidth = "",
    smLeft = "",
    ancoraRef = ancoraPopupsRef,
  }) => {
    const wrapperRef = useRef();
    if (!ancoraRef?.current || typeof document === "undefined") {
      return (
        <div
          ref={wrapperRef}
          className={`absolute bottom-full left-2 right-2 ${fullWidthOnMobile ? "" : "sm:right-auto"} ${smWidth} mb-2 flex justify-center sm:justify-start z-[${z}]`}
          style={{ marginBottom: `${mb * 0.25 * 4}px` }}
        >
          {children}
        </div>
      );
    }
    const rect = ancoraRef.current.getBoundingClientRect();
    const sm = window.matchMedia("(min-width: 640px)").matches;
    const minHeight = 340;
    const espacoAcima = rect.top;
    const espacoAbaixo = window.innerHeight - rect.bottom;
    const flipParaBaixo = espacoAcima < minHeight && espacoAbaixo > espacoAcima;
    const gapPx = mb * 8;
    const style = {
      position: "fixed",
      ...(flipParaBaixo
        ? {
            top: `${rect.bottom + window.scrollY + gapPx}px`,
            maxHeight: `calc(${espacoAbaixo}px - env(safe-area-inset-bottom) - 16px)`,
          }
        : {
            bottom: `${window.innerHeight - rect.top + gapPx}px`,
            maxHeight: `calc(${rect.top}px - env(safe-area-inset-top) - 16px)`,
          }),
      left: sm ? rect.left + window.scrollX + 8 : rect.left + window.scrollX + 8,
      right: sm ? "auto" : `${window.innerWidth - rect.right - window.scrollX + 8}px`,
      maxWidth: sm ? undefined : `${rect.width - 16}px`,
      zIndex: z,
    };
    return createPortal(
      <div
        ref={wrapperRef}
        className={`${sm ? smWidth : fullWidthOnMobile ? "w-full" : ""} ${sm ? smLeft : ""}`}
        style={style}
      >
        {children}
      </div>,
      document.body,
    );
  };

  const PortalListPopup = ({ children, z = 70, mb = 1, ancoraRef = ancoraPopupsRef }) => {
    const wrapperRef = useRef();
    if (!ancoraRef?.current || typeof document === "undefined") {
      return (
        <ul
          ref={wrapperRef}
          className={`absolute bottom-full left-0 mb-1 w-full overflow-hidden rounded-lg border bg-popover shadow-md z-[${z}]`}
          style={{ marginBottom: `${mb * 4}px` }}
        >
          {children}
        </ul>
      );
    }
    const rect = ancoraRef.current.getBoundingClientRect();
    const minHeight = 200;
    const espacoAcima = rect.top;
    const espacoAbaixo = window.innerHeight - rect.bottom;
    const flipParaBaixo = espacoAcima < minHeight && espacoAbaixo > espacoAcima;
    const gapPx = mb * 4;
    const style = {
      position: "fixed",
      ...(flipParaBaixo
        ? {
            top: `${rect.bottom + window.scrollY + gapPx}px`,
            maxHeight: `calc(${espacoAbaixo}px - env(safe-area-inset-bottom) - 16px)`,
          }
        : {
            bottom: `${window.innerHeight - rect.top + gapPx}px`,
            maxHeight: `calc(${rect.top}px - env(safe-area-inset-top) - 16px)`,
          }),
      left: `${rect.left + window.scrollX}px`,
      width: `${rect.width}px`,
      zIndex: z,
    };
    return createPortal(
      <ul
        ref={wrapperRef}
        className="overflow-hidden rounded-lg border bg-popover shadow-md"
        style={style}
      >
        {children}
      </ul>,
      document.body,
    );
  };
  const { setReplyingMessage, replyingMessage } =
    useContext(ReplyMessageContext);
  const { darkMode } = useThemeContext();

  // Grupo nunca é "aceito" por ninguém — é o motivo de ele ter aba própria —,
  // então ele vive em "aguardando". Exigir `open` para digitar trancava o
  // editor inteiro em toda conversa de grupo: dava para ler e não para
  // responder. Encerrado continua bloqueado, em grupo como em qualquer outra.
  //
  // Ticket INDIVIDUAL em "aguardando" (pending) TAMBÉM é liberado se NÃO TEM
  // DONO AINDA (userId null/undefined). Isso permite que o atendente comece a
  // digitar e enviar a primeira mensagem sem precisar clicar em "Aceitar"
  // antes; o backend assume o ticket na hora do envio. Tickets pending COM
  // dono (ex: transferido para outro atendente e ainda status não atualizou)
  // continuam bloqueados, igual antes.
  const semDonoAinda = ticket && ticket.userId === null;
  // Ticket INDIVIDUAL com status "open" OU "pending" e SEM DONO (userId null)
  // também é liberado: permite que o atendente comece a digitar sem clicar em
  // "Aceitar" primeiro; backend assume automaticamente no envio. Isso cobre
  // casos de banco antigo onde status ficou "open" mas userId continua NULL.
  const conversaAberta =
    ticketStatus === "open" ||
    (isGroup && ticketStatus === "pending") ||
    (!isGroup && ticketStatus === "pending" && semDonoAinda) ||
    (!isGroup && ticketStatus === "open" && semDonoAinda);
  // Desabilita TUDO o que vai para WhatsApp (canal cliente) quando o usuário
  // NÃO é admin nem supervisor. Para esses perfis, só funciona o switch
  // "Privado" (trava ligado) e envio por POST /messages/:id/internal.
  const soPrivado = travaPrivado === true;
  const disabled = loading || recording || !conversaAberta;
  const disabledWpp = disabled || soPrivado;

  // O nome que vai na assinatura. O alias vem do servidor já resolvido; o
  // nome do cadastro é a reserva enquanto a consulta não volta.
  const nomeDeAssinatura = String(assinatura.efetivo || user?.name || "").trim();

  // A janela de 24 h do WhatsApp oficial. `aplica: false` nos demais canais —
  // ali nunca há prazo e a barra de digitar não pode sumir.
  const [janela, setJanela] = useState({ aplica: false, aberta: true });
  const [templateAberto, setTemplateAberto] = useState(false);

  const conferirJanela = useCallback(async () => {
    if (!ticketId) return;

    try {
      const { data } = await api.get(`/meta/templates/window/${ticketId}`);
      setJanela(data);
    } catch {
      // Sem resposta, o campo de texto continua: bloquear por falha de
      // consulta impediria de responder um canal que nem tem esse limite.
      setJanela({ aplica: false, aberta: true });
    }
  }, [ticketId]);

  useEffect(() => {
    conferirJanela();
  }, [conferirJanela]);

  // Em tela estreita o texto longo do placeholder é cortado no meio.
  const [placeholderCurto, setPlaceholderCurto] = useState(
    typeof window !== "undefined" && window.innerWidth < 640
  );

  // Sair da tela no meio de uma gravação deixava o microfone aberto para
  // sempre — a luz do aparelho acesa e o aviso de "gravando" na aba.
  useEffect(() => {
    return () => {
      const atual = gravadorRef.current;
      if (!atual) return;
      try {
        if (atual.recorder.state !== "inactive") atual.recorder.stop();
      } catch {
        // Recorder já encerrado por outro caminho.
      }
      atual.stream.getTracks().forEach((trilha) => trilha.stop());
      gravadorRef.current = null;
    };
  }, []);

  useEffect(() => {
    const aoRedimensionar = () => setPlaceholderCurto(window.innerWidth < 640);
    window.addEventListener("resize", aoRedimensionar);
    return () => window.removeEventListener("resize", aoRedimensionar);
  }, []);

  useEffect(() => {
    inputRef.current.focus();
  }, [replyingMessage]);

  // Qualquer caminho que termine um "loading" de envio (sucesso, erro, timeout,
  // clique enviar, tecla Enter, upload de mídia, gravação de áudio) devolve o
  // foco para o textarea. Assim a pessoa continua digitando sem precisar clicar
  // de novo — igual WhatsApp/Telegram.
  useEffect(() => {
    if (loading) return;
    const focar = () => {
      queueMicrotask(() => inputRef.current?.focus());
      setTimeout(() => inputRef.current?.focus(), 0);
      requestAnimationFrame(() => inputRef.current?.focus());
    };
    focar();
    return () => {
      // cleanup sem ação
    };
  }, [loading]);

  useEffect(() => {
    inputRef.current.focus();
    return () => {
      setInputMessage("");
      setShowEmoji(false);
      setMedias([]);
      setReplyingMessage(null);
    };
  }, [ticketId, setReplyingMessage]);

  useEffect(() => {
    if (!showEmoji) return;
    const handleClickAway = (e) => {
      if (emojiBoxRef.current && !emojiBoxRef.current.contains(e.target)) {
        setShowEmoji(false);
      }
    };
    document.addEventListener("mousedown", handleClickAway);
    return () => document.removeEventListener("mousedown", handleClickAway);
  }, [showEmoji]);

  // Nome de assinatura desta conversa. Sem isto, a mensagem sairia sempre com
  // o nome do cadastro e os aliases não serviriam para nada.
  useEffect(() => {
    if (!ticketId) return undefined;

    const ativo = { current: true };

    api
      .get(`/user-aliases/ticket/${ticketId}`)
      .then(({ data }) => {
        if (ativo.current && data) setAssinatura(data);
      })
      .catch(() => undefined);

    return () => {
      ativo.current = false;
    };
  }, [ticketId]);

  // Participantes do grupo — alimentam a lista do "@". Em conversa individual
  // a rota devolve vazio e nada disso aparece.
  useEffect(() => {
    if (!ticketId) return undefined;

    const ativo = { current: true };

    api
      .get(`/tickets/${ticketId}/participantes`)
      .then(({ data }) => {
        if (ativo.current) setParticipantes(Array.isArray(data) ? data : []);
      })
      .catch(() => undefined);

    return () => {
      ativo.current = false;
    };
  }, [ticketId]);

  // Usuários da equipe — alimentam a lista do "@" quando o modo "Privado"
  // está ligado (nota interna entre a equipe). Em mensagem normal para o
  // cliente, a lista usada é a de participantes do grupo (WhatsApp).
  useEffect(() => {
    if (!user?.companyId) return undefined;

    const ativo = { current: true };

    (async () => {
      try {
        const { data } = await api.get("/users");
        if (!ativo.current) return;
        const lista = Array.isArray(data)
          ? data
          : Array.isArray(data?.users)
            ? data.users
            : [];
        if (ativo.current) {
          setUsersEquipe(
            lista
              .filter((u) => Number(u?.id))
              .map((u) => ({
                id: +u.id,
                name: String(u.name || u.nome || u.email || `Usuário ${u.id}`).trim(),
                email: u.email || "",
                profile: u.profile || u.role || "user",
                role: u.role || u.profile || "user",
              })),
          );
        }
      } catch (_) {
        // Sem permissão / sem network: deixa vazio, a pessoa não consegue
        // marcar ninguém mas a nota continua sendo enviada normalmente.
        if (ativo.current) setUsersEquipe([]);
      }
    })();

    return () => {
      ativo.current = false;
    };
  }, [user?.companyId]);

  // Resetar menções internas quando trocar para mensagem normal (não-privada)
  // ou quando abrir outro ticket — evita enviar menções de uma nota anterior.
  useEffect(() => {
    if (!privado) {
      setInternalMentions([]);
    }
  }, [privado]);

  useEffect(() => {
    setInternalMentions([]);
  }, [ticketId]);

  // O copiloto (no cabeçalho do ticket) manda o texto por evento, para não
  // precisar levantar o estado da caixa de mensagem até lá.
  useEffect(() => {
    const aoReceber = (evento) => {
      setInputMessage(evento.detail || "");
      inputRef.current?.focus();
    };

    document.addEventListener("copilot:usar-texto", aoReceber);
    return () => document.removeEventListener("copilot:usar-texto", aoReceber);
  }, []);

  // Carrega a lista inteira uma vez: são poucas dezenas de respostas, e a
  // faixa não pode mostrar meia lista. O serviço pagina de 20 em 20.
  useEffect(() => {
    let ativo = true;

    (async () => {
      try {
        const todas = [];
        let pagina = 1;
        let temMais = true;

        // Cinco páginas (100 respostas) é o teto: além disso a faixa deixa de
        // ser um atalho e vira uma lista, e a tela de respostas prontas é o
        // lugar dessa lista.
        while (temMais && pagina <= 5) {
          // eslint-disable-next-line no-await-in-loop
          const { data } = await api.get("/quickAnswers/", {
            params: { pageNumber: pagina },
          });

          todas.push(...(data?.quickAnswers || []));
          temMais = Boolean(data?.hasMore);
          pagina += 1;
        }

        if (ativo) setAtalhos(todas);
      } catch (err) {
        // A faixa é um extra: sem ela o "/" continua funcionando. Errar aqui
        // não pode encher a tela de aviso vermelho a cada atendimento aberto.
        if (ativo) setAtalhos([]);
      }
    })();

    return () => {
      ativo = false;
    };
  }, [recarregarAtalhos]);
  const handleChangeInput = (e) => {
    const valor = e.target.value;

    setInputMessage(valor);
    handleLoadQuickAnswer(valor);

    // "@" abre a lista de quem mencionar:
    // - PRIVADO (nota interna): usuários da equipe (usersEquipe)
    // - normal (vai para o cliente): participantes do grupo (WhatsApp)
    const temQuemMencionar = privado ? usersEquipe.length : participantes.length;
    if (temQuemMencionar) {
      const ateOCursor = valor.slice(0, e.target.selectionStart ?? valor.length);
      const arroba = /(?:^|\s)@([^\s@]*)$/.exec(ateOCursor);

      setListaDeMencao(arroba ? arroba[1].toLowerCase() : null);
    } else {
      setListaDeMencao(null);
    }

    // Apagou tudo: desistiu daquela resposta. Sem isto, escrever outra coisa
    // do zero ainda moveria o card da resposta escolhida antes.
    if (!valor) setRespostaAplicavel(null);
  };

  // Troca o "@parcial" pelo NOME do participante e guarda o jid: é a lista de
  // jids que faz o WhatsApp avisar quem foi marcado — o texto sozinho não marca
  // ninguém. O nome é o que aparece visualmente (igual o WhatsApp Web).
  const escolherMencao = (participante) => {
    const campo = inputRef.current;
    const cursor = campo?.selectionStart ?? inputMessage.length;
    const antes = inputMessage.slice(0, cursor);
    const depois = inputMessage.slice(cursor);

    const novoAntes = antes.replace(/@([^\s@]*)$/, `@${participante.nome} `);
    const novo = novoAntes + depois;

    setInputMessage(novo);
    setListaDeMencao(null);
    setMencionados((anteriores) =>
      anteriores.includes(participante.jid)
        ? anteriores
        : [...anteriores, participante.jid],
    );

    requestAnimationFrame(() => {
      campo?.focus();
      campo?.setSelectionRange(novoAntes.length, novoAntes.length);
    });
  };

  // Menção em NOTA INTERNA (Privado): marca um colega da equipe. Ao invés de
  // inserir "@Nome" no texto do input (que poluiria a mensagem enviada),
  // APENAS registra o `id` do usuário no estado `internalMentions` e renderiza
  // um chip no banner "Visível para:" ACIMA do textarea. Remove o "@parcial"
  // que a pessoa digitou para abrir o dropdown (não deixa lixo no texto).
  const escolherMencaoUsuario = (usuario) => {
    const campo = inputRef.current;
    const cursor = campo?.selectionStart ?? inputMessage.length;
    const antes = inputMessage.slice(0, cursor);
    const depois = inputMessage.slice(cursor);

    const novoAntes = antes.replace(/\s?@[^\s@]*$/, "");
    const novo = novoAntes + depois;

    setInputMessage(novo);
    setListaDeMencao(null);
    setInternalMentions((anteriores) =>
      anteriores.includes(+usuario.id)
        ? anteriores
        : [...anteriores, +usuario.id],
    );

    requestAnimationFrame(() => {
      campo?.focus();
      const novaPos = novoAntes.length;
      campo?.setSelectionRange(novaPos, novaPos);
    });
  };

  const removerMencaoUsuario = (userId) => {
    setInternalMentions((anteriores) =>
      anteriores.filter((id) => +id !== +userId),
    );
  };

  const aplicarMovimentoDaResposta = async () => {
    if (!respostaAplicavel) return;

    const { id } = respostaAplicavel;
    setRespostaAplicavel(null);

    try {
      const { data } = await api.post(`/quickAnswers/${id}/aplicar`, {
        ticketId,
      });

      // Mover em silêncio deixa a pessoa sem saber se funcionou — e não
      // mover em silêncio é pior ainda: parece defeito. Cada caso fala.
      if (data?.cartao) {
        toast.success(
          data.cartao.criado
            ? `Cartão aberto em "${data.cartao.coluna}".`
            : `Cartão movido para "${data.cartao.coluna}".`
        );
      } else if (data?.semEfeito === "ja-na-coluna") {
        toast.info(`O cartão já estava em "${data.coluna}".`);
      } else if (data?.semEfeito === "sem-vinculo") {
        toast.info(
          "Esta resposta pronta não está ligada a nenhuma coluna do Kanban."
        );
      }
    } catch (err) {
      // A mensagem já chegou ao cliente; o cartão é que não andou.
      toast.warn("A mensagem foi enviada, mas não consegui mover o cartão.");
    }
  };

  const handleQuickAnswersClick = async (resposta) => {
    setTypeBar(false);
    setInputMessage(resposta.message || "");

    setRespostaAplicavel(resposta.moveKanbanColumnId ? resposta : null);

    if (!resposta.mediaUrl) return;

    // Traz o arquivo salvo e o põe como anexo pendente, igual a quem escolhe
    // um arquivo do computador. Nada sai sozinho: a pessoa ainda revisa e
    // clica em enviar, que é a promessa da tela de respostas prontas.
    try {
      setLoading(true);
      const resposta_http = await fetch(resposta.mediaUrl);

      if (!resposta_http.ok) throw new Error("Arquivo não encontrado");

      const blob = await resposta_http.blob();
      const nome =
        resposta.mediaName || resposta.mediaUrl.split("/").pop() || "arquivo";

      setMedias([new File([blob], nome, { type: blob.type })]);
    } catch (err) {
      toast.error(
        "Não consegui carregar o arquivo desta resposta. O texto foi preenchido."
      );
    }
    setLoading(false);
  };

  const handleAddEmoji = (e) => {
    let emoji = e.native;
    setInputMessage((prevState) => prevState + emoji);
  };

  // Filtra o que o backend recusaria, dizendo o motivo por arquivo. Sem isto
  // o upload inteiro subia para só então voltar com um erro genérico.
  const aceitarArquivos = (arquivos) => {
    const bons = [];

    arquivos.forEach((arquivo) => {
      const problema = problemaDoArquivo(arquivo);

      if (problema) toast.error(`${arquivo.name}: ${problema}.`);
      else bons.push(arquivo);
    });

    return bons;
  };

  // Anexar de novo com arquivos já escolhidos ACRESCENTA, não troca: quem
  // seleciona três fotos e lembra da quarta não deveria perder as três. O
  // mesmo arquivo duas vezes (mesmo nome e tamanho) entra uma vez só.
  const adicionarMedias = (arquivos) => {
    const escolhidos = aceitarArquivos(arquivos);
    if (!escolhidos.length) return;

    setMedias((atuais) => {
      const jaTem = new Set(atuais.map((a) => `${a.name}:${a.size}`));
      const novos = escolhidos.filter((a) => !jaTem.has(`${a.name}:${a.size}`));
      return [...atuais, ...novos];
    });
  };

  const handleChangeMedias = (e) => {
    if (!e.target.files) {
      return;
    }

    adicionarMedias(Array.from(e.target.files));

    // Zera o input: escolher o mesmo arquivo de novo, depois de uma recusa,
    // não dispara `change` se o valor continuar lá.
    e.target.value = "";
  };

  const handleInputPaste = (e) => {
    if (e.clipboardData.files[0]) {
      adicionarMedias([e.clipboardData.files[0]]);
    }
  };

  // ────────────────────────────────────────────────────────────────────
  // [MENÇÕES] Confere se @Nome de fato aparece no texto que vai ser
  // enviado (antes de mandar para o backend).
  //
  // Motivo: atendente pode ter apagado manualmente "@Nome com backspace
  // depois de escolher a pessoa na lista; se continuássemos com o jid em
  // mencionados, a pessoa seria marcada à toa sem ter um "@Nome" no texto.
  //
  // Abordagem: regex case insensitive, tolerante a acentos. Normaliza nome e texto
  // para a forma decomposta (NFD) e tira os diacríticos.
  // ────────────────────────────────────────────────────────────────────
  const normalizarParaComparacao = (s) =>
    String(s || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .trim()
      .toLowerCase();

  const mençãoApareceNoTexto = (nome, texto) => {
    const n = normalizarParaComparacao(nome).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    if (!n) return false;
    const t = normalizarParaComparacao(texto || "");
    // Padrão principal: procura por "@nome" exigindo que antes tenha começo de
    // string, espaço ou whitespace não quebrável, e depois fim, espaço,
    // whitespace não quebrável, ou pontuação (vírgula, ponto, exclamação etc.)
    const re = new RegExp(
      "(^|[\\s\u00A0])@" + n + "($|[\\s\u00A0.,;:!?()\\[\\]{}\\-])",
      "i"
    );
    if (re.test(t)) return true;
    // Fallback: "@Nome grudado no final da string, ou seguido de não-word."
    const re2 = new RegExp("@" + n + "($|\\W)", "i");
    return re2.test(t);
  };

  // Filtra `mencionados[]` mantendo apenas JIDs que aparecem no texto.
  // Usa participantes[] (que tem jid,nome) para fazer a checagem.
  const filtrarMencionadosDoTexto = (textoBruto) => {
    if (!Array.isArray(mencionados) || mencionados.length === 0) return [];
    if (!Array.isArray(participantes) || participantes.length === 0) {
      return [...new Set(mencionados)].filter(Boolean);
    }
    const unicos = [...new Set(mencionados)].filter(Boolean);
    return unicos.filter((jid) => {
      const participante = participantes.find((p) => String(p?.jid || "") === String(jid || ""));
      if (!participante) return false;
      return mençãoApareceNoTexto(participante?.nome || "", textoBruto || "");
    });
  };

  // ────────────────────────────────────────────────────────────────────
  // [MENÇÕES] Substitui "@Nome" → "@Numero" no corpo que vai ser enviado
  // para o backend (e consequentemente para o WhatsApp).
  //
  // Por quê? A biblioteca Baileys/whaileys e o cliente WhatsApp do
  // receptor exigem que o TEXTO DO CORPO contenha "@<dígitos>" para
  // conseguir linkar a posição do texto com o array `mentions[]` de JIDs
  // e renderizar a tag AZUL + notificação push.
  //
  // Sem este replace: o atendente via @Nome lindo no input, a mensagem
  // chegava no WA receptor com @Nome no texto MAS (a) não ficava azul
  // clicável e (b) a pessoa mencionada NÃO recebia notificação.
  //
  // Input do atendente CONTINUA mostrando @Nome (usabilidade). A troca
  // é só na hora de submeter a requisição.
  //
  // Precisa de duas listas:
  //   * `mencionadosJids`: a lista filtrada de JIDs que realmente aparecem
  //     (evita trocar "@Pedro" se o JID dele foi removido da lista).
  //   * `participantes`: para achar (nome, numero) por jid.
  //
  // O replace é feito dos nomes MAIS LONGOS PRIMEIRO (evita colisões
  // como "Maria" dentro de "Maria Clara").
  // ────────────────────────────────────────────────────────────────────
  const substituirMencoesNomePorNumero = (textoOriginal, mencionadosJids) => {
    let saida = String(textoOriginal || "");
    if (!saida || !Array.isArray(mencionadosJids) || mencionadosJids.length === 0) {
      return saida;
    }
    if (!Array.isArray(participantes) || participantes.length === 0) return saida;

    // 1. Extrai os participantes mencionados com (nome, numero).
    const entradas = [];
    mencionadosJids.forEach((jid) => {
      const p = participantes.find((x) => String(x?.jid || "") === String(jid || ""));
      if (!p) return;
      const nome = String(p?.nome || "").trim();
      const numero = String(p?.numero || "").trim();
      if (!nome || !numero) return;
      entradas.push({ nome, numero });
    });
    if (entradas.length === 0) return saida;

    // 2. Ordem decrescente por tamanho do nome: evita "Maria" < "Maria Clara".
    entradas.sort((a, b) => b.nome.length - a.nome.length);

    // 3. Replace regex: mesma regra do mençãoApareceNoTexto (case + acentos)
    entradas.forEach(({ nome, numero }) => {
      const n = normalizarParaComparacao(nome).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      if (!n) return;

      const substituido = (matchTotal, espacoAntes, espacoDepois) => {
        const antes = espacoAntes || "";
        const depois = espacoDepois || "";
        return `${antes}@${numero}${depois}`;
      };

      // Variante 1: @Nome com espaço/pontuação antes e depois.
      const re1 = new RegExp(
        "(^|[\\s\u00A0])@" + n + "($|[\\s\u00A0.,;:!?()\\[\\]{}\\-])",
        "ig",
      );
      saida = saida.replace(re1, substituido);

      // Variante 2: @Nome grudado no final, ou seguido de char não-word.
      const re2 = new RegExp("@" + n + "($|\\W)", "ig");
      saida = saida.replace(re2, (_m, depois) => `@${numero}${depois || ""}`);
    });

    return saida;
  };

  const handleUploadMedia = async (e) => {
    setLoading(true);
    e.preventDefault();

    const legenda = inputMessage.trim();
    const mentionsIds = [...new Set(internalMentions.map((id) => +id).filter(Boolean))];
    let corpoLimpo = String(legenda || "");
    if (privado) {
      usersEquipe.forEach((u) => {
        const nome = String(u?.name || "").trim();
        if (!nome) return;
        const regex = new RegExp(
          `\\s?@${nome.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\s?`,
          "ig",
        );
        corpoLimpo = corpoLimpo.replace(regex, " ");
      });
      corpoLimpo = corpoLimpo.replace(/@[^\s@]+/g, "").replace(/\s+/g, " ").trim();
    }

    // Menções em grupo: filtra jids válidos que aparecem na legenda.
    const mencionadosValidos = !privado
      ? filtrarMencionadosDoTexto(legenda || "")
      : [];

    // [MENÇÕES] Corpo enviado ao backend: "@Nome" vira "@Numero".
    // O cliente WhatsApp receptor exige @+dígitos no texto para ligar
    // com o array mentions[] de JIDs e desenhar a tag azul + notificação.
    // A UI do input NÃO muda (atendente continua vendo @Nome).
    const legendaNumerada = !privado && mencionadosValidos.length > 0
      ? substituirMencoesNomePorNumero(legenda || "", mencionadosValidos)
      : legenda;

    const corpoFinal = privado
      ? corpoLimpo
      : legendaNumerada
        ? (signMessage ? `*${nomeDeAssinatura}:*\n${legendaNumerada}` : legendaNumerada)
        : "";

    const quotedMsgLimpo = replyingMessage?.id
      ? {
          id: replyingMessage.id,
          fromMe: Boolean(replyingMessage.fromMe),
          body: replyingMessage.body || "",
        }
      : null;

    // Monta o corpo de UMA requisição de envio.
    //
    // `midias` = arquivos que vão subir agora (multer). `mediaUrl` = arquivo
    // que JÁ está no servidor — é o que o envio em partes devolve. Nunca os
    // dois juntos.
    //
    // Legenda, menções e citação só vão na PRIMEIRA requisição: quando há
    // vários anexos o backend já reserva o texto para o primeiro balão.
    const montarFormulario = ({ midias, mediaUrl, mediaType, primeiro }) => {
      const formData = new FormData();
      formData.append("fromMe", true);

      if (midias?.length) {
        midias.forEach((media) => formData.append("medias", media));
      }

      if (mediaUrl) {
        formData.append("mediaUrl", mediaUrl);
        if (mediaType) formData.append("mediaType", mediaType);
      }

      if (!primeiro) return formData;

      if (corpoFinal) formData.append("body", corpoFinal);

      if (privado) {
        formData.append("internalMentions", JSON.stringify(mentionsIds.length ? mentionsIds : null));
      }

      if (!privado && mencionadosValidos.length > 0) {
        formData.append("mentions", JSON.stringify(mencionadosValidos));
      }

      if (quotedMsgLimpo) {
        formData.append("quotedMsg", JSON.stringify(quotedMsgLimpo));
        // eslint-disable-next-line no-console
        console.log(
          "[MessageInput:handleUploadMedia] adicionando quotedMsg no formData",
          JSON.stringify(quotedMsgLimpo),
        );
      }

      return formData;
    };

    // Sobe o arquivo em PARTES curtas e devolve o mediaUrl já remontado no
    // servidor.
    //
    // O proxy da frente (Cloudflare) responde 524 — sem entregar resposta
    // nenhuma ao navegador — quando a origem demora mais de 100 s. O POST de
    // anexo só responde no FIM de tudo (gravar + converter + entregar ao
    // WhatsApp), e num link de subida lento um vídeo de 15 MB estourava esse
    // corte só para subir. Fatiado, cada requisição termina com folga.
    const enviarArquivoEmPartes = async (arquivo) => {
      const totalPartes = Math.max(1, Math.ceil(arquivo.size / TAMANHO_PARTE_BYTES));
      const uploadId =
        typeof crypto !== "undefined" && crypto.randomUUID
          ? crypto.randomUUID()
          : `up-${Date.now()}-${Math.random().toString(16).slice(2)}`;

      let ultima = null;
      for (let indice = 0; indice < totalPartes; indice++) {
        const inicio = indice * TAMANHO_PARTE_BYTES;
        const parte = arquivo.slice(
          inicio,
          Math.min(inicio + TAMANHO_PARTE_BYTES, arquivo.size),
        );

        const formData = new FormData();
        formData.append("uploadId", uploadId);
        formData.append("chunkIndex", String(indice));
        formData.append("totalChunks", String(totalPartes));
        formData.append("fileName", arquivo.name);
        formData.append("mimeType", arquivo.type || "");
        formData.append("chunk", parte, arquivo.name);

        const { data } = await api.post(
          `/messages/${ticketId}/upload-chunk`,
          formData,
          { timeout: TIMEOUT_PARTE_MS },
        );
        ultima = data;
      }

      if (!ultima?.mediaUrl) throw new Error("ERR_UPLOAD_INCOMPLETE");
      return ultima;
    };

    // Teto maior que o padrão do axios: a resposta deste POST só chega depois
    // do arquivo terminar de subir e de ser entregue ao WhatsApp.
    const enviar = (formData) =>
      api.post(
        privado ? `/messages/${ticketId}/internal` : `/messages/${ticketId}`,
        formData,
        { timeout: TIMEOUT_ENVIO_MEDIA_MS },
      );

    try {
      const arquivos = Array.isArray(medias) ? medias : [];
      const precisaFatiar = arquivos.some(
        (arquivo) => Number(arquivo?.size || 0) > LIMITE_ENVIO_EM_PARTES_BYTES,
      );

      let resposta;
      if (!precisaFatiar) {
        // Caminho de sempre: um POST só, com todos os anexos.
        const { data } = await enviar(montarFormulario({ midias: arquivos, primeiro: true }));
        resposta = data;
      } else {
        // Um anexo por requisição. Os grandes sobem fatiados e o envio em si
        // sai pelo caminho normal do mediaUrl — o mesmo da biblioteca de mídia
        // e do encaminhar —, então a conversão e a entrega não mudam de lugar.
        const enviadas = [];
        for (let i = 0; i < arquivos.length; i++) {
          const arquivo = arquivos[i];
          const primeiro = i === 0;
          const grande = Number(arquivo?.size || 0) > LIMITE_ENVIO_EM_PARTES_BYTES;

          let formData;
          if (grande) {
            const { mediaUrl, mediaType } = await enviarArquivoEmPartes(arquivo);
            formData = montarFormulario({ mediaUrl, mediaType, primeiro });
          } else {
            formData = montarFormulario({ midias: [arquivo], primeiro });
          }

          const { data } = await enviar(formData);
          if (Array.isArray(data)) enviadas.push(...data);
          else if (data) enviadas.push(data);
        }
        resposta = enviadas.length === 1 ? enviadas[0] : enviadas;
      }

      if (resposta) {
        document.dispatchEvent(new CustomEvent("crm:mensagem-enviada", {
          detail: { ticketId, message: resposta },
        }));
        touchTicket(ticket);
      }
      setMedias([]);
      setInputMessage("");
      setReplyingMessage(null);
      aplicarMovimentoDaResposta();
    } catch (err) {
      toastError(err);
    }

    setLoading(false);
  };

  const handleSendMessage = async (opts = {}) => {
    const textoParaEnviar = typeof opts?.texto === "string"
      ? opts.texto
      : inputMessage;
    const skipSignature = Boolean(opts?.skipSignature);

    const temMidiaSelecionada = Array.isArray(medias) && medias.length > 0;
    const textoVazio = String(textoParaEnviar || "").trim() === "";

    if (textoVazio && !temMidiaSelecionada) return;

    inputRef.current?.focus();
    const devolverFoco = () => {
      queueMicrotask(() => inputRef.current?.focus());
      setTimeout(() => inputRef.current?.focus(), 0);
      requestAnimationFrame(() => inputRef.current?.focus());
    };

    setLoading(true);

    // Se tem anexo, reaproveita o pipeline do handleUploadMedia (FormData +
    // upload multer), passando a legenda atual. Evita duplicar toda a lógica
    // de tratamento de arquivo.
    if (temMidiaSelecionada) {
      try {
        await handleUploadMedia({ preventDefault: () => undefined });
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
        devolverFoco();
      }
      return;
    }

    if (privado) {
      try {
        const mentionsIds = [...new Set(internalMentions.map((id) => +id).filter(Boolean))];

        let corpoLimpo = String(textoParaEnviar || "");
        usersEquipe.forEach((u) => {
          const nome = String(u?.name || "").trim();
          if (!nome) return;
          const regex = new RegExp(
            `\\s?@${nome.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\s?`,
            "ig",
          );
          corpoLimpo = corpoLimpo.replace(regex, " ");
        });
        corpoLimpo = corpoLimpo.replace(/@[^\s@]+/g, "").replace(/\s+/g, " ").trim();

        const { data: msgCriada } = await api.post(`/messages/${ticketId}/internal`, {
          body: corpoLimpo,
          internalMentions: mentionsIds.length ? mentionsIds : null,
        });
        document.dispatchEvent(new CustomEvent("crm:mensagem-enviada", {
          detail: { ticketId, message: msgCriada },
        }));
        touchTicket(ticket);
      } catch (err) {
        toastError(err);
      } finally {
        setInputMessage("");
        setShowEmoji(false);
        setLoading(false);
        setReplyingMessage(null);
        setInternalMentions([]);
        devolverFoco();
      }
      return;
    }

    const aplicarAssinatura = skipSignature ? false : signMessage;

    const mencionadosValidos = !privado
      ? filtrarMencionadosDoTexto(textoParaEnviar || "")
      : [];

    // [MENÇÕES] Corpo enviado ao backend: "@Nome" → "@Numero".
    // WhatsApp receptor exige @dígitos no body para linkar com JIDs mentions[]
    // e renderizar tag azul + notificação push.
    // UI input do atendente NÃO é alterada (continua mostrando @Nome).
    const textoNumerado = !privado && mencionadosValidos.length > 0
      ? substituirMencoesNomePorNumero(textoParaEnviar || "", mencionadosValidos)
      : textoParaEnviar;

    const message = {
      read: 1,
      fromMe: true,
      mediaUrl: "",
      body: aplicarAssinatura
        ? `*${nomeDeAssinatura}:*\n${textoNumerado.trim()}`
        : textoNumerado.trim(),
      quotedMsg: replyingMessage,
      mentions:
        mencionadosValidos.length > 0
          ? mencionadosValidos
          : null,
    };
    try {
      const { data: respostaEnvio } = await api.post(`/messages/${ticketId}`, message);
      document.dispatchEvent(new CustomEvent("crm:mensagem-enviada", {
        detail: { ticketId, message: respostaEnvio },
      }));
      touchTicket(ticket);
      aplicarMovimentoDaResposta();
    } catch (err) {
      toastError(err);
    } finally {
      setInputMessage("");
      setShowEmoji(false);
      setLoading(false);
      setReplyingMessage(null);
      devolverFoco();
    }
  };

  useEffect(() => {
    const aoClicarBotaoRecebido = async (evento) => {
      const detail = evento?.detail || {};
      const texto = String(detail.texto || "").trim();
      if (!texto) return;
      const skipSignature = Boolean(detail.skipSignature);
      setInputMessage(texto);
      await handleSendMessage({ texto, skipSignature });
    };

    document.addEventListener("clicar-botao-recebido", aoClicarBotaoRecebido);
    return () => document.removeEventListener("clicar-botao-recebido", aoClicarBotaoRecebido);
  }, [handleSendMessage]);

  const handleLoadQuickAnswer = async (value) => {
    if (value && value.indexOf("/") === 0) {
      try {
        const { data } = await api.get("/quickAnswers/", {
          // `value` é o texto recém-digitado; `inputMessage` ainda tem o valor
          // anterior neste ponto do render e faria a busca ficar um passo atrás.
          params: { searchParam: value.substring(1) },
        });
        setQuickAnswer(data.quickAnswers);
        setTypeBar(data.quickAnswers.length > 0);
      } catch (err) {
        setTypeBar(false);
      }
    } else {
      setTypeBar(false);
    }
  };

  const handleStartRecording = async () => {
    setLoading(true);
    try {
      // Sem HTTPS o navegador nem expõe o microfone. Acontece direto em
      // produção servida por HTTP, e o erro que aparecia era um
      // "undefined is not a function" que não dizia nada.
      if (!navigator.mediaDevices?.getUserMedia) {
        toast.error(
          "O navegador só libera o microfone em conexão segura (https). " +
            "Peça para quem cuida do servidor habilitar o certificado."
        );
        setLoading(false);
        return;
      }

      const formato = escolherFormato();

      if (!formato) {
        toast.error("Este navegador não sabe gravar áudio.");
        setLoading(false);
        return;
      }

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      const recorder = new MediaRecorder(
        stream,
        formato.mime ? { mimeType: formato.mime } : undefined
      );

      pedacosRef.current = [];
      // Guarda o stream junto: parar o recorder não desliga o microfone, e
      // sem fechar as trilhas a luz do aparelho fica acesa depois da gravação.
      gravadorRef.current = { recorder, stream, ext: formato.ext };

      recorder.ondataavailable = (evento) => {
        if (evento.data && evento.data.size > 0) {
          pedacosRef.current.push(evento.data);
        }
      };

      // Um pedaço por segundo: sem o intervalo, o navegador só entrega tudo
      // no fim, e uma aba fechada no meio levaria a gravação inteira.
      recorder.start(1000);

      setRecording(true);
      setLoading(false);
    } catch (err) {
      // Permissão negada é o caso mais comum e não é defeito do sistema:
      // merece instrução, não a mensagem técnica do navegador.
      if (err?.name === "NotAllowedError") {
        toast.error(
          "Você bloqueou o microfone para este site. Libere no cadeado ao " +
            "lado do endereço e tente de novo."
        );
      } else if (err?.name === "NotFoundError") {
        toast.error("Nenhum microfone encontrado neste computador.");
      } else {
        toastError(err);
      }
      setLoading(false);
    }
  };

  // Encerra a gravação e devolve o áudio. Fecha o microfone em qualquer
  // caso — inclusive quando a pessoa desiste.
  const encerrarGravacao = () =>
    new Promise((resolve) => {
      const atual = gravadorRef.current;

      if (!atual) {
        resolve(null);
        return;
      }

      const finalizar = () => {
        atual.stream.getTracks().forEach((trilha) => trilha.stop());
        gravadorRef.current = null;

        const blob = new Blob(pedacosRef.current, {
          type: atual.recorder.mimeType || "audio/webm"
        });
        pedacosRef.current = [];

        resolve({ blob, ext: atual.ext });
      };

      if (atual.recorder.state === "inactive") {
        finalizar();
        return;
      }

      atual.recorder.onstop = finalizar;
      atual.recorder.stop();
    });

  const handleUploadAudio = async () => {
    setLoading(true);
    try {
      const gravado = await encerrarGravacao();

      if (!gravado) {
        setRecording(false);
        setLoading(false);
        return;
      }

      // Áudio curto demais era descartado em silêncio: a pessoa gravava,
      // soltava, e simplesmente não acontecia nada — parecia microfone
      // quebrado. Agora ela sabe por que não foi.
      if (gravado.blob.size < 2000) {
        toast.info("Gravação muito curta. Segure o botão enquanto fala.");
        setLoading(false);
        setRecording(false);
        return;
      }

      // Converte para WAV antes de mandar: o formato do MediaRecorder não
      // declara a duração, e a biblioteca do WhatsApp precisa dela para
      // montar a mensagem de voz.
      const wav = await paraWav(gravado.blob);

      const formData = new FormData();
      const filename = `${new Date().getTime()}.wav`;
      formData.append("medias", wav, filename);
      // Sem legenda: mandar o nome do arquivo aqui fazia o áudio chegar no
      // celular do cliente com "1785342981.mp3" escrito embaixo, e a lista de
      // atendimentos mostrar isso como prévia da conversa.
      formData.append("body", "");
      formData.append("fromMe", true);

      await api.post(`/messages/${ticketId}`, formData);
    } catch (err) {
      toastError(err);
    }

    setRecording(false);
    setLoading(false);
  };

  const handleCancelAudio = async () => {
    await encerrarGravacao();
    setRecording(false);
  };

  const MiniImagemReply = ({ src }) => {
    const [erro, setErro] = useState(false);
    if (erro || !src) {
      return (
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-l-md bg-black/10">
          <ImageIcon className="h-5 w-5 text-muted-foreground/60" />
        </div>
      );
    }
    return (
      <img
        src={src}
        alt=""
        loading="lazy"
        decoding="async"
        className="h-12 w-12 shrink-0 rounded-l-md object-cover"
        onError={() => setErro(true)}
      />
    );
  };

  const MiniStickerReply = ({ src }) => {
    const [erro, setErro] = useState(false);
    if (erro || !src) {
      return (
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-l-md" style={{ background: "transparent" }}>
          <Sticker className="h-5 w-5 text-muted-foreground/70" />
        </div>
      );
    }
    return (
      <img
        src={src}
        alt=""
        loading="lazy"
        decoding="async"
        className="h-12 w-12 shrink-0 rounded-l-md object-contain"
        style={{ background: "transparent" }}
        onError={() => setErro(true)}
      />
    );
  };

  const ReplyThumb = (message) => {
    if (!message) return null;
    const urlMedia = message.mediaUrl ? getPublicFileUrl(message.mediaUrl) : "";
    const tipo = message.mediaType || "";
    const ext = urlMedia ? extensaoDe(urlMedia) : "";
    const ehAudio = tipo === "audio";
    const ehVideo = tipo === "video" || [".mp4", ".webm", ".mov", ".m4v", ".ogv", ".3gp", ".mkv", ".avi"].includes(ext);
    const ehSticker = tipo === "sticker";
    const ehImage = tipo === "image" || [".jpg", ".jpeg", ".jfif", ".png", ".gif", ".webp", ".bmp", ".heic", ".heif", ".tiff", ".avif"].includes(ext);
    const ehDoc = tipo === "document" || (!ehAudio && !ehVideo && !ehSticker && !ehImage && message.mediaUrl);
    const ehLocation = tipo === "location";
    const ehVcard = tipo === "vcard" || tipo === "contact";
    const iconWrapper = (children, cor) => (
      <div className={cn("flex h-12 w-12 shrink-0 items-center justify-center rounded-l-md", cor)}>
        {children}
      </div>
    );
    if (ehSticker && urlMedia) return <MiniStickerReply src={urlMedia} />;
    if (ehImage && urlMedia) return <MiniImagemReply src={urlMedia} />;
    if (ehVideo && urlMedia) return iconWrapper(<VideoIcon className="h-5 w-5 text-white" />, "bg-black/70");
    if (ehAudio) return iconWrapper(<Music className="h-5 w-5 text-white" />, "bg-emerald-500/80");
    if (ehLocation) return iconWrapper(<MapPin className="h-5 w-5 text-white" />, "bg-red-500/80");
    if (ehVcard) return iconWrapper(<UserRound className="h-5 w-5 text-white" />, "bg-sky-500/80");
    if (ehDoc) {
      if (ext === ".pdf") return iconWrapper(<FileText className="h-5 w-5 text-white" />, "bg-red-500/80");
      if ([".xls", ".xlsx", ".csv"].includes(ext)) return iconWrapper(<FileSpreadsheet className="h-5 w-5 text-white" />, "bg-emerald-500/80");
      if ([".zip", ".rar", ".7z", ".tar", ".gz"].includes(ext)) return iconWrapper(<FileArchive className="h-5 w-5 text-white" />, "bg-amber-500/80");
      return iconWrapper(<FileIcon className="h-5 w-5 text-white" />, "bg-slate-500/80");
    }
    return null;
  };

  const renderReplyingMessage = (message) => {
    const thumb = ReplyThumb(message);
    return (
      <div className="flex w-full items-center gap-2 px-3 pt-2">
        <div
          className={cn(
            "flex flex-1 overflow-hidden rounded-lg",
            !message.fromMe ? "bg-emerald-100 dark:bg-emerald-900/30" : "bg-black/5 dark:bg-white/10"
          )}
        >
          <span
            className={cn("w-1 shrink-0", !message.fromMe ? "bg-emerald-400" : "bg-sky-400")}
          />
          {thumb}
          <div className="min-w-0 flex-1 whitespace-pre-wrap p-2.5 text-sm">
            {!message.fromMe && (
              <span className="flex font-medium text-sky-500">
                {message.contact?.name}
              </span>
            )}
            <div className="line-clamp-2 break-words text-xs sm:text-[13px]">
              {message.body}
            </div>
          </div>
        </div>
        <Button
          variant="ghost"
          size="icon"
          disabled={loading || !conversaAberta}
          onClick={() => setReplyingMessage(null)}
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
    );
  };

  // Troca o nome de assinatura DESTA conversa. Fica no ticket, não na tela:
  // quem assumir depois continua de onde o cliente parou, e o alias não volta
  // ao anterior a cada F5.
  const trocarAlias = async (aliasId) => {
    try {
      const { data } = await api.put(`/user-aliases/ticket/${ticketId}`, {
        aliasId,
      });

      if (data) {
        const { data: atual } = await api.get(
          `/user-aliases/ticket/${ticketId}`,
        );
        setAssinatura(atual);
      }
    } catch (err) {
      toastError(err);
    }
  };

  const signToggle = (
    <div className="flex items-center gap-1.5">
      {/* Privado: a mensagem fica na conversa para a equipe e NÃO sai para o
          cliente. O aviso visual é o que evita o engano de mandar a anotação
          interna para quem está do outro lado. */}
      <Switch
        id="mensagemPrivada"
        checked={privado}
        disabled={travaPrivado}
        onCheckedChange={setPrivado}
        onClick={(e) => {
          if (travaPrivado) {
            e.preventDefault();
            e.stopPropagation();
          }
        }}
        className={cn(privado && "data-[state=checked]:bg-amber-500")}
      />
      <Label
        htmlFor={travaPrivado ? undefined : "mensagemPrivada"}
        className={cn(
          "flex items-center gap-1 text-xs font-normal text-muted-foreground",
          travaPrivado && "pointer-events-none opacity-80",
          privado && "font-medium text-amber-600 dark:text-amber-400",
        )}
        title={
          travaPrivado
            ? "Seu perfil só pode enviar notas privadas. Converse com o administrador."
            : ""
        }
      >
        <Lock className="h-3 w-3" />
        Privado
        {travaPrivado && (
          <span className="ml-1 text-[10px] text-muted-foreground/80">
            (obrigatório)
          </span>
        )}
      </Label>

      <span className="mx-1 h-4 w-px bg-border" />

      <Switch
        id="signMessage"
        checked={signMessage}
        disabled={switchAssinarDesabilitado}
        onClick={(e) => {
          if (switchAssinarDesabilitado) {
            e.preventDefault();
            e.stopPropagation();
          }
        }}
        onCheckedChange={(checked) => setSignMessage(checked)}
      />
      <Label
        htmlFor={switchAssinarDesabilitado ? undefined : "signMessage"}
        className={cn(
          "text-xs font-normal text-muted-foreground",
          switchAssinarDesabilitado && "pointer-events-none opacity-60",
          !switchAssinarDesabilitado && "cursor-pointer select-none",
        )}
        title={
          travaBloqueiaAssinatura
            ? "Sua empresa bloqueou a desativação da assinatura para atendentes"
            : forceSignMessagesEnabled === undefined && isAtendenteComum
              ? "Carregando configurações…"
              : ""
        }
      >
        {i18n.t("messagesInput.signMessage")}
      </Label>

      {/* Com nomes alternativos cadastrados, dá para trocar no meio da
          conversa — é o mesmo atendente falando por outra frente. Quando a
          empresa trava a assinatura, o menu de troca de nome continua
          aparecendo: a pessoa só não pode DESLIGAR a assinatura, trocar o
          nome pode. */}
      {signMessage && assinatura.opcoes.length > 0 && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 gap-1 px-1.5 text-xs text-muted-foreground"
              title="Trocar o nome da assinatura nesta conversa"
            >
              como <span className="font-medium">{nomeDeAssinatura}</span>
              <ChevronDown className="h-3 w-3" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            <DropdownMenuItem onSelect={() => trocarAlias(null)}>
              {assinatura.aliasId ? "" : "✓ "}
              {user?.name} (cadastro)
            </DropdownMenuItem>
            {assinatura.opcoes.map((alias) => (
              <DropdownMenuItem
                key={alias.id}
                onSelect={() => trocarAlias(alias.id)}
              >
                {assinatura.aliasId === alias.id ? "✓ " : ""}
                {alias.name}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );

  // A prévia dos anexos é uma janela por cima da conversa: dá para ver todos
  // os arquivos escolhidos, grandes, antes de mandar.
  const previaDeAnexos = medias.length > 0 && (
    <PreviaDeAnexos
      arquivos={medias}
      aoMudar={setMedias}
      aoAdicionar={adicionarMedias}
      legenda={inputMessage}
      aoMudarLegenda={setInputMessage}
      aoEnviar={handleUploadMedia}
      aoCancelar={() => {
        setMedias([]);
        setInputMessage("");
      }}
      enviando={loading}
    />
  );

  // Janela de 24 h fechada: texto livre não sai, e insistir gasta o erro
  // 131047 da Meta depois de a mensagem já aparecer como enviada aqui. O
  // caminho passa a ser o modelo aprovado, e a barra some para deixar isso
  // claro em vez de deixar alguém digitar no vazio.
  if (janela.aplica && !janela.aberta && conversaAberta) {
    return (
      <div className="flex flex-none flex-col gap-2 border-t bg-amber-500/10 p-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))]">
        <div className="flex items-start gap-2 text-sm">
          <Clock className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
          <p>
            Passaram-se mais de 24 h desde a última mensagem deste cliente. O
            WhatsApp só aceita modelo aprovado agora — enviar um reabre a
            conversa por mais 24 h.
          </p>
        </div>

        <Button
          className="self-start"
          onClick={() => setTemplateAberto(true)}
          disabled={loading}
        >
          <FileText className="h-4 w-4" />
          Enviar modelo
        </Button>

        <EnviarTemplateModal
          open={templateAberto}
          ticketId={ticketId}
          onClose={() => setTemplateAberto(false)}
          onEnviado={conferirJanela}
        />
      </div>
    );
  }

  return (
    <div
      className={cn(
        // `@container`: quem decide se a fita de ferramentas aparece aberta ou
        // vira o "⋮" é a largura DESTA barra, não a da janela. Com o breakpoint
        // de viewport (`sm:`), uma tela de 1440px abria a fita inteira — 662px
        // de botões — dentro de um painel de conversa de 796px, e o campo de
        // texto, único `flex-1` da linha, ficava com 34px: dava para digitar
        // sem enxergar o que estava sendo escrito.
        "@container relative flex flex-none flex-col border-t bg-muted/40 pb-[env(safe-area-inset-bottom)]",
        arrastandoArquivo && "bg-primary/10 ring-1 ring-inset ring-primary/40",
      )}
      // Arrastar o arquivo do computador para a barra anexa direto — de lá
      // ele cai na prévia, igual ao que veio pelo clipe.
      onDragOver={(e) => {
        if (disabled) return;
        e.preventDefault();
        setArrastandoArquivo(true);
      }}
      onDragLeave={() => setArrastandoArquivo(false)}
      onDrop={(e) => {
        if (disabled) return;
        e.preventDefault();
        setArrastandoArquivo(false);
        const soltos = Array.from(e.dataTransfer?.files || []);
        if (soltos.length) adicionarMedias(soltos);
      }}
    >
      {soPrivado && (
        <div className="mb-3 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-600/40 dark:bg-amber-950/30 dark:text-amber-300">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <div className="font-medium">
              Este atendimento está atribuído a outro atendente.
            </div>
            <div className="mt-0.5 opacity-90">
              Você só pode enviar <strong>notas internas (Privado)</strong> para
              a equipe. O switch Privado está ligado automaticamente abaixo.
              Para responder diretamente ao cliente, clique em{" "}
              <strong>Aceitar</strong> primeiro no topo.
            </div>
          </div>
        </div>
      )}
      {previaDeAnexos}

      {/* A foto ou o vídeo da câmera entram na prévia dos anexos, como
          qualquer arquivo escolhido: dá para conferir e escrever legenda. */}
      {contatoAberto && (
        <EnviarContatoModal
          aberto
          aoFechar={() => setContatoAberto(false)}
          ticketId={ticketId}
        />
      )}

      {cameraAberta && (
        <CameraModal
          aberto
          aoFechar={() => setCameraAberta(false)}
          aoCapturar={(arquivo) => adicionarMedias([arquivo])}
        />
      )}

      {/* Montado só quando abre: assim cada envio começa com os campos
          limpos, sem precisar de efeito para zerar o formulário. */}
      {localizacaoAberta && (
        <EnviarLocalizacaoModal
          aberto
          aoFechar={() => setLocalizacaoAberta(false)}
          ticketId={ticketId}
        />
      )}

      <MediaLibraryPicker
        open={libraryOpen}
        onClose={() => setLibraryOpen(false)}
        ticketId={ticketId}
        quotedMsg={replyingMessage}
      />

      <EnviarTemplateModal
        open={templateAberto}
        ticketId={ticketId}
        onClose={() => setTemplateAberto(false)}
        onEnviado={conferirJanela}
      />

      {/* Fora das barras desktop/mobile: assim o seletor funciona nos dois. */}
      {showEmoji && (
        <PortalPopup ancoraRef={ancoraBarrasRef} z={70} mb={2} smLeft="left-2 sm:right-auto sm:justify-start sm:w-auto" smWidth="sm:w-[380px]" fullWidthOnMobile={true}>
          <div
            ref={emojiBoxRef}
            className="w-full max-w-full overflow-x-auto rounded-lg shadow-lg"
          >
            <Picker
              data={emojiData}
              locale="pt"
              perLine={8}
              previewPosition="none"
              skinTonePosition="none"
              theme={darkMode ? "dark" : "light"}
              onEmojiSelect={handleAddEmoji}
            />
          </div>
        </PortalPopup>
      )}
      <FigurinhaPicker
        aberto={figurinhasAberto}
        aoFechar={() => setFigurinhasAberto(false)}
        ticketId={ticketId}
        desabilitado={disabled}
        ancoraRef={ancoraBarrasRef}
        quotedMsg={replyingMessage}
      />

      {replyingMessage && renderReplyingMessage(replyingMessage)}

      {/* Faixa de atalhos: as respostas prontas à mão, sem precisar saber que
          existe um "/". Só o atalho aparece — o texto inteiro de vinte
          respostas não caberia, e é pelo atalho que a equipe as conhece.
          Clicar preenche o campo; enviar continua sendo decisão de quem
          atende, que é a promessa da tela de respostas prontas. */}
      {conversaAberta && atalhos.length > 0 && (
        <div className="flex items-center gap-1 border-b px-2 py-1.5">
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 shrink-0"
            aria-label={
              atalhosAbertos ? "Esconder respostas prontas" : "Mostrar respostas prontas"
            }
            title={
              atalhosAbertos ? "Esconder respostas prontas" : "Mostrar respostas prontas"
            }
            aria-expanded={atalhosAbertos}
            onClick={() => setAtalhosAbertos((atual) => !atual)}
          >
            <Zap
              className={cn(
                "h-4 w-4",
                atalhosAbertos ? "text-primary" : "text-muted-foreground",
              )}
            />
          </Button>

          {atalhosAbertos ? (
            <div className="flex flex-1 items-center gap-1 overflow-x-auto scrollbar-none pr-1">
              <div className="flex shrink-0 flex-1 items-center gap-1 overflow-x-auto scrollbar-none">
                {atalhos.map((resposta) => (
                  <button
                    key={resposta.id}
                    type="button"
                    disabled={disabled}
                    title={resposta.message || resposta.mediaName || resposta.shortcut}
                    onClick={() => {
                      setQrInicial(resposta);
                      setQrEditandoId(resposta.id);
                      setQrAberto(true);
                    }}
                    className="flex shrink-0 items-center gap-1 rounded-full border bg-background px-3 py-1 text-xs font-medium hover:bg-accent disabled:opacity-50"
                  >
                    {resposta.mediaUrl && (
                      <Paperclip className="h-3 w-3 text-muted-foreground" />
                    )}
                    <span className="max-w-[180px] truncate">{resposta.shortcut}</span>
                  </button>
                ))}
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={disabled}
                onClick={() => {
                  setQrInicial(null);
                  setQrEditandoId(null);
                  setQrAberto(true);
                }}
                className="ml-auto h-7 shrink-0 gap-1 border border-dashed border-muted-foreground/30 rounded-full px-2.5 text-[11px] text-muted-foreground hover:bg-accent hover:text-foreground"
                title="Criar nova resposta pronta"
              >
                <Plus className="h-3.5 w-3.5" />
                Nova
              </Button>
            </div>
          ) : (
            <div className="flex min-w-0 flex-1 items-center justify-between gap-2">
              <span className="text-xs text-muted-foreground truncate">
                {atalhos.length} resposta{atalhos.length > 1 ? "s" : ""} pronta
                {atalhos.length > 1 ? "s" : ""}
              </span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={disabled}
                onClick={() => {
                  setQrInicial(null);
                  setQrEditandoId(null);
                  setQrAberto(true);
                }}
                className="h-7 shrink-0 gap-1 rounded-full px-2.5 text-[11px] text-muted-foreground hover:bg-accent hover:text-foreground"
              >
                <Plus className="h-3.5 w-3.5" />
                Nova
              </Button>
            </div>
          )}
        </div>
      )}

      {formatacaoAberta && (
        <BarraDeFormatacao
          campoRef={inputRef}
          texto={inputMessage}
          aoMudar={setInputMessage}
          desabilitado={disabled}
        />
      )}

      <div ref={ancoraBarrasRef} className="flex w-full items-center gap-1 p-2">
        {/* 662px de botões + IA + microfone + folga só sobram campo de texto
            legível a partir de ~1024px de barra; abaixo disso tudo isto vira o
            "⋮" ao lado, que já leva os mesmos itens. */}
        <div className="hidden items-center gap-0.5 @5xl:flex">
          <Button
            variant="ghost"
            size="icon"
            disabled={disabledWpp}
            aria-label="Emoji"
            onClick={() => setShowEmoji((prev) => !prev)}
          >
            <Smile className="h-5 w-5 text-muted-foreground" />
          </Button>

          <input
            multiple
            type="file"
            id="upload-button"
            className="hidden"
            disabled={disabledWpp}
            // O `accept` filtra o que aparece no seletor do sistema; a
            // validação acima é o que realmente barra, porque o accept é
            // uma sugestão e o usuário pode ignorá-la.
            accept={ACCEPT_UPLOAD}
            onChange={handleChangeMedias}
          />
          <label htmlFor="upload-button">
            <Button variant="ghost" size="icon" asChild disabled={disabledWpp}>
              <span aria-label="Anexar arquivo">
                <Paperclip className="h-5 w-5 text-muted-foreground" />
              </span>
            </Button>
          </label>

          <Button
            variant="ghost"
            size="icon"
            disabled={disabledWpp}
            aria-label="Enviar da biblioteca"
            onClick={() => setLibraryOpen(true)}
          >
            <Images className="h-5 w-5 text-muted-foreground" />
          </Button>

          <Button
            variant="ghost"
            size="icon"
            disabled={disabledWpp}
            aria-label="Figurinhas"
            title="Figurinhas"
            onClick={() => setFigurinhasAberto((atual) => !atual)}
          >
            <Sticker className="h-5 w-5 text-muted-foreground" />
          </Button>

          <Button
            variant="ghost"
            size="icon"
            disabled={disabledWpp}
            aria-label="Tirar foto ou gravar vídeo"
            title="Câmera"
            onClick={() => setCameraAberta(true)}
          >
            <Camera className="h-5 w-5 text-muted-foreground" />
          </Button>

          <Button
            variant="ghost"
            size="icon"
            disabled={disabledWpp}
            aria-label="Enviar contato"
            title="Enviar contato"
            onClick={() => setContatoAberto(true)}
          >
            <UserRound className="h-5 w-5 text-muted-foreground" />
          </Button>

          <Button
            variant="ghost"
            size="icon"
            disabled={disabledWpp}
            aria-label="Enviar localização"
            title="Enviar localização"
            onClick={() => setLocalizacaoAberta(true)}
          >
            <MapPin className="h-5 w-5 text-muted-foreground" />
          </Button>

          <Button
            variant="ghost"
            size="icon"
            disabled={disabled}
            aria-label="Formatação do texto"
            title="Negrito, itálico, link"
            aria-pressed={formatacaoAberta}
            onClick={() => setFormatacaoAberta((v) => !v)}
            className={cn(formatacaoAberta && "text-primary")}
          >
            <Type className="h-5 w-5" />
          </Button>

          <div className="ml-1 mr-2">{signToggle}</div>
        </div>

        <div className="flex @5xl:hidden">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" aria-label="Mais opções">
                <MoreVertical className="h-5 w-5 text-muted-foreground" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              <DropdownMenuItem
                onSelect={() => {
                  setShowEmoji((prev) => {
                    const novo = !prev;
                    if (novo) setFigurinhasAberto(false);
                    return novo;
                  });
                }}
                disabled={disabled}
              >
                <Smile className="h-4 w-4" /> Emoji
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <label htmlFor="upload-button" className="flex cursor-pointer items-center gap-2">
                  <Paperclip className="h-4 w-4" /> Anexar
                </label>
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={() => {
                  setLibraryOpen(true);
                }}
                disabled={disabled}
              >
                <Images className="h-4 w-4" /> Biblioteca
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={() => {
                  setFigurinhasAberto((atual) => {
                    const novo = !atual;
                    if (novo) setShowEmoji(false);
                    return novo;
                  });
                }}
                disabled={disabled}
              >
                <Sticker className="h-4 w-4" /> Figurinhas
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={() => {
                  setCameraAberta(true);
                }}
                disabled={disabled}
              >
                <Camera className="h-4 w-4" /> Câmera
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={() => {
                  setContatoAberto(true);
                }}
                disabled={disabled}
              >
                <UserRound className="h-4 w-4" /> Enviar contato
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={() => {
                  setFormatacaoAberta((v) => !v);
                }}
                disabled={disabled}
              >
                <Type className="h-4 w-4" /> Formatação
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={() => {
                  setLocalizacaoAberta(true);
                }}
                disabled={disabled}
              >
                <MapPin className="h-4 w-4" /> Localização
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                {signToggle}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* `min-w-0`: sem isto o campo não encolhe abaixo do próprio conteúdo
            e empurra os botões para fora da barra. */}
        <div ref={ancoraPopupsRef} className="relative min-w-0 flex-1">
          {privado && internalMentions.length > 0 && (
            <div className="mx-0.5 mb-2 flex flex-wrap items-center gap-x-1.5 gap-y-1 rounded-2xl border border-solid border-amber-200/80 bg-amber-50 px-3.5 py-2 text-[13px] text-stone-700 shadow-[0_1px_2px_rgba(0,0,0,0.04)] dark:bg-amber-950/40 dark:border-amber-800/60 dark:text-stone-200">
              <Eye className="mr-1 h-3.5 w-3.5 shrink-0 opacity-80" />
              <span className="mr-1 font-semibold">
                Visível para:
              </span>
              <div className="flex flex-wrap items-center gap-1">
                {internalMentions.map((uid) => {
                  const u = usersEquipe.find((x) => +x.id === +uid);
                  if (!u) return null;
                  const rawProfile = String(u?.profile || u?.role || "user").toLowerCase();
                  const perfilLabel =
                    rawProfile === "admin" || rawProfile === "administrator"
                      ? "admin"
                      : rawProfile === "supervisor"
                      ? "supervisor"
                      : "atendente";
                  const nome = String(u.name || "").trim() || `Usuário ${u.id}`;
                  return (
                    <span
                      key={`chip-mention-${u.id}`}
                      className="inline-flex items-center gap-1 rounded-full border border-amber-300/70 bg-white/80 px-2.5 py-0.5 text-[12px] font-medium text-stone-700 shadow-sm dark:bg-stone-800/80 dark:border-amber-700/60 dark:text-stone-100"
                    >
                      <span className="truncate">{nome}</span>
                      <span className="rounded-full bg-amber-100/90 px-1.5 py-[1px] text-[10px] font-semibold uppercase tracking-wide text-amber-800 dark:bg-amber-900/70 dark:text-amber-200">
                        {perfilLabel}
                      </span>
                      <button
                        type="button"
                        onClick={() => removerMencaoUsuario(u.id)}
                        className="ml-0.5 inline-flex h-4 w-4 items-center justify-center rounded-full text-stone-500 hover:bg-amber-200/80 hover:text-stone-800 dark:hover:bg-amber-800/70 dark:hover:text-stone-100"
                        aria-label={`Remover ${nome} da visibilidade`}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  );
                })}
              </div>
            </div>
          )}
          <textarea
            ref={inputRef}
            rows={1}
            placeholder={
              !conversaAberta
                ? i18n.t("messagesInput.placeholderClosed")
                : privado
                ? "Mensagem privada — o cliente não vê. Selecione o atendente digite @"
                : placeholderCurto
                ? "Mensagem"
                : i18n.t("messagesInput.placeholderOpen")
            }
            value={inputMessage}
            onChange={handleChangeInput}
            disabled={recording || loading || !conversaAberta}
            onPaste={(e) => {
              conversaAberta && handleInputPaste(e);
            }}
            onKeyPress={(e) => {
              if (loading || e.shiftKey) return;
              else if (e.key === "Enter") {
                e.preventDefault();
                handleSendMessage();
              }
            }}
            onKeyDown={(e) => {
              // Atalhos de formatação, os mesmos de qualquer editor.
              if (!(e.ctrlKey || e.metaKey)) return;

              const marca =
                e.key.toLowerCase() === "b"
                  ? "*"
                  : e.key.toLowerCase() === "i"
                    ? "_"
                    : "";

              if (!marca) return;

              e.preventDefault();
              aplicarMarca(inputRef.current, inputMessage, marca, setInputMessage);
            }}
            className={cn(
              "max-h-32 w-full resize-none overflow-y-auto rounded-2xl border bg-background px-4 py-2.5 text-sm shadow-xs scrollbar-none placeholder:text-muted-foreground focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-60 [&::-webkit-scrollbar]:hidden",
              privado &&
                "border-amber-400 bg-amber-50 focus-visible:ring-amber-400 dark:bg-amber-950/30",
            )}
          />
          {/* Lista do "@":
              - PRIVADO: usuários da equipe CRM (que acompanharão a nota)
              - normal para o cliente: participantes do grupo (WhatsApp) */}
          {listaDeMencao !== null && (
            <PortalListPopup ancoraRef={ancoraPopupsRef} z={70} mb={1}>
              {privado
                ? usersEquipe
                    .filter(
                      (u) =>
                        !listaDeMencao ||
                        String(u.name || "")
                          .toLowerCase()
                          .includes(listaDeMencao) ||
                        String(u.email || "")
                          .toLowerCase()
                          .includes(listaDeMencao),
                    )
                    .slice(0, 30)
                    .map((u) => (
                      <li
                        key={`mention-user-${u.id}`}
                        className="list-none"
                      >
                        <button
                          type="button"
                          onClick={() => escolherMencaoUsuario(u)}
                          className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-accent"
                        >
                          <span className="min-w-0">
                            <span className="block truncate">
                              {u.name || `Usuário ${u.id}`}
                            </span>
                            <span className="block truncate text-xs text-muted-foreground">
                              {internalMentions.includes(+u.id)
                                ? "já marcado nesta nota"
                                : u.email || `#${u.id}`}
                            </span>
                          </span>
                          <span
                            className={cn(
                              "shrink-0 rounded-full px-2 py-0.5 text-[10px] uppercase",
                              internalMentions.includes(+u.id)
                                ? "bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-300"
                                : "bg-muted text-muted-foreground",
                            )}
                          >
                            {internalMentions.includes(+u.id)
                              ? "incluído"
                              : "equipe"}
                          </span>
                        </button>
                      </li>
                    ))
                : participantes
                    .filter(
                      (p) =>
                        !listaDeMencao ||
                        p.nome.toLowerCase().includes(listaDeMencao) ||
                        p.numero.includes(listaDeMencao),
                    )
                    .slice(0, 30)
                    .map((p) => (
                      <li key={p.jid} className="list-none">
                        <button
                          type="button"
                          onClick={() => escolherMencao(p)}
                          className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-accent"
                        >
                          <span className="min-w-0">
                            <span className="block truncate">{p.nome}</span>
                            <span className="block truncate text-xs text-muted-foreground">
                              {p.numero}
                            </span>
                          </span>
                          {p.admin && (
                            <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-[10px] uppercase text-muted-foreground">
                              admin
                            </span>
                          )}
                        </button>
                      </li>
                    ))}
            </PortalListPopup>
          )}

          {typeBar && (
            <PortalListPopup ancoraRef={ancoraPopupsRef} z={70} mb={1}>
              {quickAnswers.map((value, index) => (
                <li key={index} className="list-none">
                  <button
                    type="button"
                    onClick={() => handleQuickAnswersClick(value)}
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-accent"
                  >
                    {value.mediaUrl && (
                      <Paperclip className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                    )}
                    <span className="truncate">
                      {value.message
                        ? `${value.shortcut} - ${value.message}`
                        : `${value.shortcut} - ${value.mediaName || "arquivo"}`}
                    </span>
                  </button>
                </li>
              ))}
            </PortalListPopup>
          )}
        </div>

        {/* Correção e sugestão ficam colados no campo, e não num painel à
            parte: é aqui que a pessoa está escrevendo quando precisa delas. */}
        <AssistenteDeTexto
          ticketId={ticketId}
          texto={inputMessage}
          aoTrocarTexto={setInputMessage}
          desabilitado={recording || loading || !conversaAberta}
        />

        {inputMessage ? (
          <Button
            size="icon"
            className="shrink-0 rounded-full"
            aria-label="Enviar mensagem"
            // Impede o botão de tirar o foco do textarea. Sem isto o blur
            // acontece antes do clique e o teclado do celular já começa a
            // fechar — o focus() do handler chegaria tarde.
            onPointerDown={(e) => e.preventDefault()}
            onClick={handleSendMessage}
            disabled={loading}
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </Button>
        ) : recording ? (
          <div className="flex items-center">
            <Button variant="ghost" size="icon" disabled={loading} onClick={handleCancelAudio}>
              <XCircle className="h-5 w-5 text-destructive" />
            </Button>
            {loading ? (
              <Loader2 className="h-5 w-5 animate-spin text-emerald-500" />
            ) : (
              <RecordingTimer />
            )}
            <Button variant="ghost" size="icon" onClick={handleUploadAudio} disabled={loading}>
              <CheckCircle2 className="h-5 w-5 text-emerald-500" />
            </Button>
          </div>
        ) : (
        <Button
          variant="ghost"
          size="icon"
          disabled={loading || !conversaAberta}
          onClick={handleStartRecording}
          aria-label="Gravar áudio"
        >
          <Mic className="h-5 w-5 text-muted-foreground" />
        </Button>
      )}
      </div>

      <Dialog open={seletorNotificarAberto} onOpenChange={(aberto) => {
        // Ao fechar com ESC/overlay fora, cancelar a seleção temporária
        if (aberto === false) setMencionadosTemp([]);
        setSeletorNotificarAberto(aberto);
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-1.5">
              <Eye className="h-4 w-4 text-amber-500" />
              Notificar atendente (visível na nota privada)
            </DialogTitle>
            <DialogDescription className="text-[13px]">
              Selecione os atendentes que poderão ver esta nota interna. O ticket
              aparecerá na aba <span className="font-semibold">Aguardando</span>{" "}
              deles com o aviso <Badge variant="secondary" className="mx-0.5 align-middle text-[10px] uppercase">Mensagem privada</Badge>
              e eles só poderão responder com outra nota privada.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-80 space-y-1 overflow-y-auto border rounded-lg p-2">
            {usersEquipe.length === 0 && (
              <div className="text-xs text-muted-foreground text-center py-3">
                Nenhum atendente carregado.
              </div>
            )}
            {usersEquipe.map((u) => {
              if (Number(u?.id) === Number(user?.id)) return null;
              const rawP = String(u?.profile || u?.role || "user").toLowerCase();
              const perfilTag =
                rawP === "admin" || rawP === "administrator"
                  ? "admin"
                  : rawP === "supervisor"
                    ? "supervisor"
                    : "atendente";
              const checked = mencionadosTemp.includes(+u.id);
              return (
                <label
                  key={`sel-ment-${u.id}`}
                  className={cn(
                    "flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm",
                    checked ? "bg-amber-50 dark:bg-amber-950/30" : "hover:bg-accent",
                  )}
                >
                  <Checkbox
                    aria-label={`Selecionar ${u.name || ""}`}
                    checked={checked}
                    onCheckedChange={(chec) => {
                      setMencionadosTemp((atual) =>
                        chec === true
                          ? atual.includes(+u.id) ? atual : [...atual, +u.id]
                          : atual.filter((id) => +id !== +u.id),
                      );
                    }}
                  />
                  <span className="truncate font-medium">{u.name}</span>
                  <Badge
                    variant="outline"
                    className="ml-auto text-[10px] uppercase tracking-wide"
                  >
                    {perfilTag}
                  </Badge>
                </label>
              );
            })}
          </div>
          <DialogFooter className="flex flex-col sm:flex-row sm:justify-between sm:space-x-2">
            <Button
              variant="ghost"
              type="button"
              onClick={() => {
                setMencionadosTemp([]);
                setSeletorNotificarAberto(false);
              }}
            >
              Cancelar (sem notificar)
            </Button>
            <Button
              type="button"
              onClick={() => {
                // Aplica os IDs selecionados no banner / envio final.
                const uniq = [...new Set(mencionadosTemp.map((id) => +id).filter(Boolean))];
                setInternalMentions(uniq);
                setSeletorNotificarAberto(false);
                setTimeout(() => inputRef.current?.focus(), 50);
              }}
            >
              <Check className="mr-1 h-4 w-4" />
              Confirmar {mencionadosTemp.length > 0 && `(${mencionadosTemp.length})`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <QuickAnswersModal
        open={qrAberto}
        onClose={() => {
          setQrAberto(false);
          setQrEditandoId(null);
          setQrInicial(null);
        }}
        quickAnswerId={qrEditandoId || undefined}
        initialValues={qrInicial || undefined}
        onUse={(resposta) => {
          handleQuickAnswersClick(resposta);
        }}
        onSave={async () => {
          // Atualiza a faixa de atalhos com a resposta nova / editada.
          await recarregarAtalhos();
        }}
      />
    </div>
  );
};

export default MessageInput;
